import { GiftConfig } from '@/@types/global';
import { debounce } from '@/entrypoints/utils';
import { createLogger } from '@/lib/logger';
import { MiniCart } from '../elements/mini-cart';
import { MiniCartGiftsAccordion, PreviewGift } from '../elements/mini-cart-gifts-accordion';
import { Cart, CartEvents, CartLine, UpdateCartPayload } from '../internals/core';
import { CartEventBus } from '../internals/event-bus';

const logger = createLogger('mini-cart:free-gifts');

let giftsConfig: GiftConfig[] = [];
let miniCartElement: MiniCart | null = null;
let isModifyingCart = false;

declare global {
  interface Window {
    Currency?: {
      convert: (amount: number, from: string, to: string) => number;
      rates?: Record<string, number>;
    };
  }
}

function getActiveCurrencyCode(): string | null {
  const currency = window.Shopify?.currency;
  const active = currency?.active;
  return typeof active === 'string' && active.trim() !== '' ? active : null;
}

function getShopBaseCurrencyCode(): string | null {
  const themeShopCurrency = (window.theme as { shopCurrency?: string } | undefined)?.shopCurrency;
  if (typeof themeShopCurrency === 'string' && themeShopCurrency.trim() !== '') {
    return themeShopCurrency;
  }

  const active = window.Shopify?.currency?.active;
  return typeof active === 'string' && active.trim() !== '' ? active : null;
}

function convertToShopBaseCurrency(amount: number): number {
  const from = getActiveCurrencyCode();
  const to = getShopBaseCurrencyCode();

  if (!from || !to || from === to) return amount;

  const converter = window.Currency;
  if (!converter || typeof converter.convert !== 'function') return amount;

  try {
    const converted = converter.convert(amount, from, to);
    return typeof converted === 'number' && !Number.isNaN(converted) ? converted : amount;
  } catch (e) {
    logger.warn('Failed to convert currency using currencies.js', { from, to, amount, e });
    return amount;
  }
}

/**
 * Parses a money-formatted string into a numeric value.
 *
 * @param raw Money string such as `"$1,010.00"` or `"1.010,00 EUR"`.
 * @returns Parsed numeric value, or `0` when parsing fails.
 */
function parseMoneyString(raw: string): number {
  const cleaned = raw.replace(/[^\d.,-]/g, '');

  if (!cleaned) return 0;

  const commaCount = (cleaned.match(/,/g) || []).length;
  const dotCount = (cleaned.match(/\./g) || []).length;
  let normalized = cleaned;

  if (commaCount > 0 && dotCount > 0) {
    const lastComma = cleaned.lastIndexOf(',');
    const lastDot = cleaned.lastIndexOf('.');

    if (lastComma > lastDot) {
      normalized = cleaned.replace(/\./g, '').replace(/,/g, '.');
    } else {
      normalized = cleaned.replace(/,/g, '');
    }
  } else if (commaCount > 0) {
    const commas = [...cleaned.matchAll(/,/g)].map((match) => match.index ?? -1);
    const allGroupingCommas = commas.every((commaIndex) => {
      if (commaIndex < 0) return false;
      const digitsAfterComma = cleaned.slice(commaIndex + 1).match(/^\d{3}(?:,|$)/);
      return Boolean(digitsAfterComma);
    });

    if (allGroupingCommas) {
      normalized = cleaned.replace(/,/g, '');
    } else {
      const lastComma = cleaned.lastIndexOf(',');
      const beforeDecimal = cleaned.slice(0, lastComma).replace(/,/g, '');
      const afterDecimal = cleaned.slice(lastComma + 1);
      normalized = `${beforeDecimal}.${afterDecimal}`;
    }
  } else if (dotCount > 1) {
    const lastDot = cleaned.lastIndexOf('.');
    const beforeDecimal = cleaned.slice(0, lastDot).replace(/\./g, '');
    const afterDecimal = cleaned.slice(lastDot + 1);
    normalized = `${beforeDecimal}.${afterDecimal}`;
  }

  const asNumber = parseFloat(normalized);
  return Number.isNaN(asNumber) ? 0 : asNumber;
}

function mapGiftToAddItem(gift: GiftConfig) {
  return {
    id: gift.gift_variant.id,
    quantity: 1,
    properties: {
      __free_gift: 'true',
      _source: 'mini-cart:free-gifts',
    },
  };
}

async function addGiftsToCart(gifts: GiftConfig[]) {
  if (gifts.length === 0 || isModifyingCart) return;

  if (!miniCartElement) {
    miniCartElement = document.querySelector<MiniCart>('mini-cart');
  }

  if (!miniCartElement) {
    logger.error('Mini cart element not found while adding gifts');
    return;
  }

  isModifyingCart = true;

  try {
    await miniCartElement.cartApi.addItems(gifts.map((gift) => mapGiftToAddItem(gift)));
  } catch (error) {
    logger.error('Failed to add free gifts to cart', error);
  } finally {
    isModifyingCart = false;
  }
}

async function removeGiftsFromCart(gifts: CartLine[]) {
  if (gifts.length === 0 || isModifyingCart) return;

  if (!miniCartElement) {
    miniCartElement = document.querySelector<MiniCart>('mini-cart');
  }

  if (!miniCartElement) {
    logger.error('Mini cart element not found while adding gifts');
    return;
  }

  isModifyingCart = true;

  try {
    const changePayload: Extract<UpdateCartPayload, { updates: Record<number, number> }> = {
      updates: gifts.reduce(
        (acc, gift) => {
          acc[gift.id] = 0;
          return acc;
        },
        {} as Record<number, number>,
      ),
    };
    await miniCartElement.cartApi.updateItems(changePayload);
  } catch (error) {
    logger.error('Failed to add free gifts to cart', error);
  } finally {
    isModifyingCart = false;
  }
}

function onCartChange(cart: Cart) {
  const isGiftItem = (item: CartLine) =>
      item?.properties?.__free_gift === 'true' ||
      Boolean(giftsConfig.find((gift) => gift.gift_variant.id === item.variant_id)),
    ecomProdcuts = cart.items.filter(
      (item) => item.vendor.toLowerCase().indexOf('medispa') === -1 && !isGiftItem(item),
    ),
    cartValueDollars = ecomProdcuts.reduce((total, item) => {
      const itemDollars = (item.final_line_price ?? 0) / 100;
      return total + convertToShopBaseCurrency(itemDollars);
    }, 0),
    giftsInCart = cart.items.filter((item) => isGiftItem(item)),
    eligibleGifts =
      cartValueDollars === 0
        ? []
        : giftsConfig.filter((gift) => cartValueDollars >= gift.price_threshold);

  // Two tiers can point at the same variant, and the carousel keys on variant,
  // so collapse them before previewing. Mirrors the checkout chain.
  const uniqueEligibleGifts = Array.from(
    new Map(eligibleGifts.map((gift) => [gift.gift_variant.id, gift])).values(),
  );

  // Gifts already on the cart are rendered from the preview API's own response,
  // so previewing them again would show the same gift twice.
  const variantIdsInCart = new Set(giftsInCart.map((item) => item.variant_id));
  const giftsToPreview = uniqueEligibleGifts.filter(
    (gift) => !variantIdsInCart.has(gift.gift_variant.id),
  );

  const miniCartGiftsAccordion = document.querySelector<MiniCartGiftsAccordion>(
    'mini-cart-gifts-accordion',
  );

  if (miniCartGiftsAccordion) {
    const getPrice = (price: string) => {
      const barePriceMatch = /^\s*[^\w\d<>-]?\s*(-?\d+(?:[\d.,]*\d)?)\s*(?:[A-Z]{3})?\s*$/.exec(
        price,
      );
      if (barePriceMatch && price.indexOf('<') === -1 && price.indexOf('>') === -1) {
        return parseMoneyString(barePriceMatch[1]);
      }

      const match = />([^<]+)</.exec(price);
      if (!match) return 0;
      return parseMoneyString(match[1]);
    };

    const formattedGifts = giftsToPreview.map(
      (gift) =>
        ({
          id: `gid://shopify/Product/${gift.gift_variant.id}`,
          product_title: gift.gift_variant.product_title,
          variant_title: gift.gift_variant.variant_title,
          featured_image: gift.gift_variant.featured_image,
          sku: gift.gift_variant.sku,
          price: getPrice(gift.gift_variant.price).toString(),
          compare_at_price: getPrice(
            gift.gift_variant.compare_at_price || gift.gift_variant.price,
          ).toString(),
          quantity: 1,
        }) satisfies PreviewGift,
    );

    // Replace rather than merge. updateAdditionalGifts only drops what it is
    // explicitly handed with quantity 0, and that removal list could only ever
    // be built from gifts already on the cart - so a preview-only gift that
    // stopped qualifying was never named and stayed in the carousel.
    miniCartGiftsAccordion.setAdditionalGifts(formattedGifts);
  }
}

const debouncedOnCartChange = debounce(onCartChange, 200);

function addEligibleGiftsToCarousel(eventBus: CartEventBus) {
  eventBus.on(CartEvents.DebouncedChange, (event) => debouncedOnCartChange(event.detail.cart));

  const initialLoad = async () => {
    miniCartElement = document.querySelector<MiniCart>('mini-cart');
    const cart = await miniCartElement?.cartApi.get();

    if (!cart) {
      logger.error('Cart not found');
      return;
    }

    window.addCheckoutEventChain(async (cart: Cart) => {
      if (!miniCartElement) return cart;
      if (isModifyingCart) return cart;

      const isGiftItem = (item: any) =>
        item?.properties?.__free_gift === 'true' ||
        Boolean(giftsConfig.find((gift) => gift.gift_variant.id === item.variant_id));

      const ecomProducts = cart.items.filter(
        (item) => item.vendor.toLowerCase().indexOf('medispa') === -1 && !isGiftItem(item),
      );
      const cartValueDollars = ecomProducts.reduce((total, item) => {
        const itemDollars = (item.final_line_price ?? 0) / 100;
        return total + convertToShopBaseCurrency(itemDollars);
      }, 0);
      const rawEligibleGifts =
        cartValueDollars === 0
          ? []
          : giftsConfig.filter((gift) => cartValueDollars >= gift.price_threshold);

      const uniqueEligibleGiftsByVariantId = new Map<number, GiftConfig>();
      rawEligibleGifts.forEach((gift) => {
        uniqueEligibleGiftsByVariantId.set(gift.gift_variant.id, gift);
      });
      const eligibleGifts = Array.from(uniqueEligibleGiftsByVariantId.values());

      const eligibleGiftVariantIds = new Set<number>(eligibleGifts.map((g) => g.gift_variant.id));

      const giftsInCart = cart.items.filter((item: any) => isGiftItem(item));

      const updates: Record<number, number> = {};
      const seenGiftVariantIds = new Set<number>();

      giftsInCart.forEach((giftLine: any) => {
        const variantId = giftLine.variant_id as number;

        if (!eligibleGiftVariantIds.has(variantId)) {
          updates[giftLine.id] = 0;
          return;
        }

        if (seenGiftVariantIds.has(variantId)) {
          updates[giftLine.id] = 0;
          return;
        }

        seenGiftVariantIds.add(variantId);

        if (typeof giftLine.quantity === 'number' && giftLine.quantity !== 1) {
          updates[giftLine.id] = 1;
        }
      });

      const giftsToAdd = eligibleGifts.filter(
        (gift) => !seenGiftVariantIds.has(gift.gift_variant.id),
      );

      if (Object.keys(updates).length === 0 && giftsToAdd.length === 0) return cart;

      isModifyingCart = true;
      try {
        let updatedCart: Cart = cart;

        if (Object.keys(updates).length > 0) {
          const changePayload: Extract<UpdateCartPayload, { updates: Record<number, number> }> = {
            updates,
          };
          updatedCart = await miniCartElement.cartApi.updateItems(changePayload);
        }

        if (giftsToAdd.length > 0) {
          logger.info('Adding eligible gifts to cart', giftsToAdd);
          updatedCart = await miniCartElement.cartApi.addItems(
            giftsToAdd.map((gift) => mapGiftToAddItem(gift)),
          );
        }

        return updatedCart;
      } catch (error) {
        logger.error('Failed to modify free gifts during checkout chain', error);
        return cart;
      } finally {
        isModifyingCart = false;
      }
    });

    eventBus.on(CartEvents.UIOpen, () => {
      onCartChange(cart);
    });
  };

  initialLoad();
}

function prepareEnvironment() {
  miniCartElement = document.querySelector<MiniCart>('mini-cart');

  if (!miniCartElement) {
    logger.error('Mini cart element not found');
    return;
  }

  const eventBus = miniCartElement.bus;

  miniCartElement.execOnReady(() => {
    giftsConfig = [...window.miniCartGiftsConfig];

    // Sort by price threshold ascending
    giftsConfig = giftsConfig.sort((a, b) => a.price_threshold - b.price_threshold);

    addEligibleGiftsToCarousel(eventBus);
  });
}

if (document.readyState === 'complete') {
  prepareEnvironment();
} else {
  document.addEventListener('DOMContentLoaded', prepareEnvironment);
}
